import React, { useState, useEffect, useCallback } from 'react';
import { FileSignature, DollarSign, Users, TrendingUp, Search, Eye, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContractTierManagement = () => {
  const { token } = useAuth();
  const { toast } = useToast();
  const [contractUsers, setContractUsers] = useState([]);
  const [commissionTransactions, setCommissionTransactions] = useState([]);
  const [totalCommission, setTotalCommission] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [viewMode, setViewMode] = useState('users'); // 'users' or 'transactions'

  const fetchContractUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/subscriptions/admin/contract-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContractUsers(response.data.contract_users || []);
      setTotalCommission(response.data.total_commission_earned || 0);
    } catch (error) {
      console.error('Error fetching contract users:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load contract tier users',
      });
    }
  }, [token, toast]);

  const fetchCommissionTransactions = useCallback(async (userId = null) => {
    try {
      let url = `${API_URL}/api/subscriptions/admin/commission-transactions`;
      if (userId) {
        url += `?user_id=${userId}`;
      }
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCommissionTransactions(response.data.transactions || []);
    } catch (error) {
      console.error('Error fetching commission transactions:', error);
    }
  }, [token]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchContractUsers(), fetchCommissionTransactions()]);
      setLoading(false);
    };
    loadData();
  }, [fetchContractUsers, fetchCommissionTransactions]);

  const handleViewUserTransactions = (userId) => {
    setSelectedUser(userId);
    fetchCommissionTransactions(userId);
    setViewMode('transactions');
  };

  const handleBackToUsers = () => {
    setSelectedUser(null);
    fetchCommissionTransactions();
    setViewMode('users');
  };

  const filteredUsers = contractUsers.filter(item => {
    const user = item.user || {};
    const searchLower = searchTerm.toLowerCase();
    return (
      user.username?.toLowerCase().includes(searchLower) ||
      user.email?.toLowerCase().includes(searchLower) ||
      user.display_name?.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <div className="text-center py-20">
        <RefreshCw className="w-8 h-8 text-emerald-400 animate-spin mx-auto mb-4" />
        <p className="text-gray-400">Loading contract tier data...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
            <FileSignature className="w-8 h-8 text-emerald-400" />
            The Contract Tier
          </h2>
          <p className="text-gray-400 mt-1">Manage users with 20% revenue share agreement</p>
        </div>
        <Button
          onClick={() => {
            fetchContractUsers();
            fetchCommissionTransactions();
          }}
          variant="outline"
          className="border-gray-600 hover:bg-gray-700 text-white"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 border-emerald-500/50">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center">
                <Users className="w-6 h-6 text-emerald-400" />
              </div>
              <div>
                <p className="text-gray-400 text-sm">Contract Users</p>
                <p className="text-3xl font-bold text-white">{contractUsers.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-green-500/50">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <p className="text-gray-400 text-sm">Total Commission Earned</p>
                <p className="text-3xl font-bold text-green-400">${totalCommission.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border-purple-500/50">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <p className="text-gray-400 text-sm">Commission Rate</p>
                <p className="text-3xl font-bold text-purple-400">20%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* View Toggle */}
      <div className="flex gap-2">
        <Button
          onClick={() => handleBackToUsers()}
          variant={viewMode === 'users' ? 'default' : 'outline'}
          className={viewMode === 'users' 
            ? 'bg-emerald-500 hover:bg-emerald-600 text-white' 
            : 'border-gray-600 text-gray-400 hover:text-white'}
        >
          <Users className="w-4 h-4 mr-2" />
          Users
        </Button>
        <Button
          onClick={() => setViewMode('transactions')}
          variant={viewMode === 'transactions' ? 'default' : 'outline'}
          className={viewMode === 'transactions' 
            ? 'bg-emerald-500 hover:bg-emerald-600 text-white' 
            : 'border-gray-600 text-gray-400 hover:text-white'}
        >
          <DollarSign className="w-4 h-4 mr-2" />
          All Transactions
        </Button>
      </div>

      {viewMode === 'users' && (
        <>
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              placeholder="Search contract users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-gray-800 border-gray-700 text-white"
            />
          </div>

          {/* Users List */}
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white">Contract Tier Users ({filteredUsers.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {filteredUsers.length === 0 ? (
                <div className="text-center py-10">
                  <FileSignature className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-400">No users on The Contract tier yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredUsers.map((item) => {
                    const user = item.user || {};
                    const stats = item.commission_stats || {};
                    return (
                      <div
                        key={user.id || user.username}
                        className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 p-4 bg-gray-800/50 rounded-lg border border-gray-700"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-full bg-gradient-to-r from-emerald-400 to-cyan-500 flex items-center justify-center text-white font-bold">
                            {user.username?.[0]?.toUpperCase() || '?'}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-semibold text-white">{user.username || 'Unknown'}</p>
                              <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/50">
                                Contract
                              </Badge>
                            </div>
                            <p className="text-sm text-gray-400">{user.email || 'No email'}</p>
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-6 text-sm">
                          <div>
                            <p className="text-gray-400">Total Sales</p>
                            <p className="font-bold text-white">${stats.total_sales?.toFixed(2) || '0.00'}</p>
                          </div>
                          <div>
                            <p className="text-gray-400">Commission Paid</p>
                            <p className="font-bold text-green-400">${stats.total_commission?.toFixed(2) || '0.00'}</p>
                          </div>
                          <div>
                            <p className="text-gray-400">Transactions</p>
                            <p className="font-bold text-white">{stats.transaction_count || 0}</p>
                          </div>
                          <Button
                            onClick={() => handleViewUserTransactions(user.id)}
                            size="sm"
                            variant="outline"
                            className="border-emerald-500 text-emerald-400 hover:bg-emerald-500/10"
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {viewMode === 'transactions' && (
        <Card className="bg-gray-900 border-gray-800">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-white">
              {selectedUser ? 'User Transactions' : 'All Commission Transactions'}
            </CardTitle>
            {selectedUser && (
              <Button
                onClick={handleBackToUsers}
                variant="outline"
                size="sm"
                className="border-gray-600 text-gray-400 hover:text-white"
              >
                Back to Users
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {commissionTransactions.length === 0 ? (
              <div className="text-center py-10">
                <DollarSign className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400">No commission transactions yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="text-left py-3 px-4 text-gray-400 font-medium">Date</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium">Type</th>
                      <th className="text-right py-3 px-4 text-gray-400 font-medium">Sale Amount</th>
                      <th className="text-right py-3 px-4 text-gray-400 font-medium">Commission (20%)</th>
                      <th className="text-right py-3 px-4 text-gray-400 font-medium">Seller Earnings</th>
                    </tr>
                  </thead>
                  <tbody>
                    {commissionTransactions.map((tx) => (
                      <tr key={tx.id || tx.created_at} className="border-b border-gray-800 hover:bg-gray-800/50">
                        <td className="py-3 px-4 text-white">
                          {new Date(tx.created_at).toLocaleDateString()}
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant="outline" className="border-gray-600 text-gray-300">
                            {tx.transaction_type || 'Sale'}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-right text-white font-medium">
                          ${tx.sale_amount?.toFixed(2) || '0.00'}
                        </td>
                        <td className="py-3 px-4 text-right text-green-400 font-medium">
                          ${tx.commission_amount?.toFixed(2) || '0.00'}
                        </td>
                        <td className="py-3 px-4 text-right text-white">
                          ${tx.seller_earnings?.toFixed(2) || '0.00'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ContractTierManagement;
